import { Action } from 'redux';
import { ActionsObservable, StateObservable } from 'redux-observable';
import { actionSuccess, actionFailure, ActionResult } from '../actions/Common';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { from, Observable, NEVER } from 'rxjs';
import { gaException, gaTiming } from '../analytics';

export const onTryGA = async (action: Action, onTry: () => Promise<any>) => {
    try {
        const start = performance.now();
        const result = await onTry();
        const end = performance.now();
        gaTiming({ 
            category: 'Redux', 
            var: action.type,
            value: end - start  
        });
        return result;
    } catch (err) {
        gaException({ 
            description: `${action.type} error: ${err.message}`, 
            fatal: false 
        });
        throw err;
    }
};

/** Factory for creating Redux Epics that execute a single async function
 *  per action. Returns observable of ActionResult.
 *  NB: requires ActionResult be included in the type declaration for action.
 */
export const createTryActionsEpic = <T extends Action, S, R>(
    actionType: string,
    onTry: (action: T, store: S) => Promise<R>,
    onSuccess?: (action: T, result: R) => any,
    onError?: (action: T) => any
) =>
    (action$: ActionsObservable<T>, store: StateObservable<S>): Observable<ActionResult> =>
        action$.ofType(actionType).pipe(
            mergeMap(action =>
                from(onTryGA(action, () => onTry(action, store.value)))
                    .pipe(
                        map(result => actionSuccess(action, onSuccess ? onSuccess(action, result) : undefined)),
                        catchError(error => ActionsObservable.of(actionFailure(action, error, onError ? onError(action) : undefined))))));

export const createTryActionFromStoreEpic = <T extends Action, S, R>(
    loadAction: (store: S) => T | null,
    onTry: (action: T, store: S) => Promise<R>,
    onSuccess?: (action: T, result: R) => any,
    onError?: (action: T) => any
) => (action$: ActionsObservable<T>, store: StateObservable<S>): Observable<ActionResult> => {
    const action = loadAction(store.value);
    if (action) {
        return from(onTry(action, store.value))
            .pipe( 
                map(result => actionSuccess(action, onSuccess ? onSuccess(action, result) : undefined)),
                catchError(error => ActionsObservable.of(actionFailure(action, error, onError ? onError(action) : undefined))));
    } else {
        return NEVER;
    }
};